import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { useParams, Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiClock,
  FiPhone,
  FiCopy,
  FiUser,
  FiTruck,
  FiMapPin,
  FiX,
  FiCheck, // Import the check icon
} from "react-icons/fi";
import { LuChefHat } from "react-icons/lu";
import axiosInstance from "../config/axios";
import { OrderGrubhubURL, CompletedOrderURL } from "../../component/config/config";
import "../../assets/css/dishdetails.css";

const DishDetails = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null); // State for copy feedback

  useEffect(() => {
    const fetchOrderDetails = async () => {
      document.body.classList.add("loading-indicator");
      if (!orderId) {
        setError("Order ID is missing.");
        setLoading(false);
        document.body.classList.remove("loading-indicator");
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const searchPayload = {
          order_id: orderId,
          pageSize: 1,
          pageNumber: 1,
        };

        const openOrderResponse = await axiosInstance().post(
          OrderGrubhubURL,
          searchPayload
        );

        let orderData = openOrderResponse.data?.data?.[0];

        if (!orderData) {
          const completedOrderResponse = await axiosInstance().post(
            CompletedOrderURL,
            searchPayload
          );
          orderData = completedOrderResponse.data.data?.[0];
        }

        if (orderData) {
          setOrder(orderData);
        } else {
          setError(`Order with ID #${orderId} not found.`);
        }
      } catch (err) {
        setError("Could not load order details. Please try again.");
      } finally {
        setLoading(false);
        document.body.classList.remove("loading-indicator");
      }
    };
    fetchOrderDetails();
  }, [orderId]);

  const handleCopy = (text, key) => {
    if (text && text !== "N/A") {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopiedKey(key);
          setTimeout(() => setCopiedKey(null), 2000); // Revert after 2 seconds
        })
        .catch((err) => console.error("Failed to copy text: ", err));
    }
  };

  const getStatusText = (order) => {
    const latestEventType = order?.currentDeliveryStatus?.events?.[0]?.type;
    const lastWebhookEventType = order?.deliveryWebhookStatus?.events?.[0]?.type;
    switch (latestEventType) {
      case "CREATED":
      case "ASSIGNED":
      case "COURIER_AT_PICKUP":
      case "CANCELLED":
      case "UNASSIGNED":
        return "Awaiting Pick-Up";
      case "PICKED_UP":
      case "IN_TRANSIT":
      case "COURIER_AT_DROPOFF":
        return "Out For Delivery";
      case "DELIVERED":
      case "PROOF_OF_DELIVERY":  
        return "Delivered";
      default:
        if (order.status === 1 || order.currentDeliveryStatus === "GrubHub-Cancel") {
          return "Awaiting Pick-Up";
        }
        else if (order.currentDeliveryStatus === "GrubHub-Refund" && (lastWebhookEventType === "DELIVERED" || lastWebhookEventType === "PROOF_OF_DELIVERY")) {
          return "Delivered";
        }
        else {
          return "Order Not Ready";
        }
    }
  };
  
  const getAddress = (addressObj) => {
    if (!addressObj) return null;
    const parts = [
      addressObj.add1 || addressObj.address1,
      addressObj.add2 || addressObj.address2,
      addressObj.city,
      addressObj.state,
      addressObj.zip || addressObj.zipCode,
    ];
    return parts.filter((p) => p).join(", ") || null;
  };

  const renderCopyIcon = (uniqueKey) => {
    return copiedKey === uniqueKey ? (
      <>
        <FiCheck style={{ color: "#28a745" }} />
        <span className="copied-tooltip">Copied!</span>
      </>
    ) : (
      <FiCopy />
    );
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <h6>Loading Order Details...</h6>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dash-R">
        <div className="alert alert-danger m-5">{error}</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="dash-R">
        <div className="dash-head">
          <h3>Order Not Found</h3>
        </div>
        <div className="dash-bottom mt-2">
          <p>The requested order could not be found.</p>
        </div>
      </div>
    );
  }

  const user = order.userId || order.user;
  const chef = order.chefId || order.chef;

  const latestEventType = order?.currentDeliveryStatus?.events?.[0]?.type;
  const isCancelledOrUnassigned = ["UNASSIGNED" , "CANCELED"].includes(latestEventType);

  const customerAddressObject = order.userAddress
    ? order.userAddress
    : {
        add1: user?.address1,
        add2: user?.address2,
        city: user?.city,
        state: user?.state,
        zip: user?.zipCode,
      };

  const recipientAddress = getAddress(customerAddressObject);
  const chefAddress = getAddress(chef);

  return (
    <div className="dash-R">
      <div className="delivery-tracking-page">
        <Container>
          <div className="d-flex justify-content-start mb-3">
            <Link to="/dashboard/myorder/delivery" className="back-link">
              <FiArrowLeft />
              <span>Back</span>
            </Link>
          </div>
          <div className="page-header">
            {recipientAddress && <h1 className="header-title">Delivery to {recipientAddress}</h1>}
            {order.currentDeliveryStatus != null && (
              <div className="header-eta">
                <FiClock />
                <span>ETA: {!isCancelledOrUnassigned && order.etaMinutes != null ? `${order.etaMinutes} mins` : "N/A"}</span>
                <FiX className="close-icon" />
              </div>
            )}
          </div>

          {
            order.tracking_url && !isCancelledOrUnassigned ? (
              <Card className="map-card">
                <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="map-placeholder-link">
                  <div className="map-placeholder-content">
                    <h2 className="map-title">Click to view live tracking</h2>
                    {(chefAddress && recipientAddress) && <p className="map-route">{chefAddress} &rarr; {recipientAddress}</p>}
                    <div className="open-map-icon"><FiMapPin size={40} /></div>
                    <p className="map-disclaimer">(The live map will open securely in a new tab)</p>
                  </div>
                </a>
              </Card>
            ) : (
              <Card className="map-card">
                <Card.Body className="text-center">
                  <h2 className="map-title">Live tracking not available</h2>
                  {(chefAddress && recipientAddress) && <p className="map-route">{chefAddress} &rarr; {recipientAddress}</p>}
                </Card.Body>
              </Card>
            )
          }

          <Row className="info-cards-row">
            <Col lg={6} className="mb-4">
              <Card className="info-card">
                <Card.Body>
                  <div className="info-header">
                    <LuChefHat className="card-icon" />
                    {chef?.fullName && <span className="info-title">WannaEat Chef - {chef.fullName}</span>}
                  </div>
                  {chefAddress && (
                    <div className="info-content">
                      <p className="info-label">Pickup location:</p>
                      <p className="info-value">{chefAddress}</p>
                    </div>
                  )}
                  {chef?.phoneNumber && (
                    <div className="info-footer">
                      <div className="phone-info"><FiPhone /><span>{chef.phoneNumber}</span></div>
                      <div className="copy-icon-circle" onClick={() => handleCopy(chef.phoneNumber, 'chefPhone')}>
                        {renderCopyIcon('chefPhone')}
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6} className="mb-4">
              {order.currentDeliveryStatus != null && (
                <Card className="info-card">
                  <Card.Body>
                    <div className="info-header"><FiTruck className="card-icon" /><span className="info-title">Driver</span></div>
                    <div className="driver-details">
                      <div className="driver-avatar"><span>{!isCancelledOrUnassigned && order.driverDetails?.name ? order.driverDetails.name.charAt(0) : "D"}</span></div>
                      <div className="driver-infoo">
                        <p className="info-value">{!isCancelledOrUnassigned && order.driverDetails?.name ? order.driverDetails.name : "N/A"}</p>
                        <p className="info-label vehicle-info"><FiTruck size={16} /> {!isCancelledOrUnassigned && order.driverDetails?.delivery_method ? order.driverDetails.delivery_method : "N/A"}</p>
                        <div className="info-footer">
                          <div className="phone-info"><FiPhone /><span>{!isCancelledOrUnassigned && order.driverDetails?.phone_number ? order.driverDetails.phone_number : "N/A"}</span></div>
                          {!isCancelledOrUnassigned && order.driverDetails?.phone_number && (
                            <div className="copy-icon-circle" onClick={() => handleCopy(order.driverDetails.phone_number, 'driverPhone')}>
                              {renderCopyIcon('driverPhone')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>

            <Col lg={6} className="mb-4">
              <Card className="info-card">
                <Card.Body>
                  <div className="info-header"><FiUser className="card-icon" /><span className="info-title">Recipient</span></div>
                  <div className="info-content">
                    {user?.fullName && <p className="info-value">{user.fullName}</p>}
                    {recipientAddress && <p className="info-label address-info"><FiMapPin size={16} /> {recipientAddress}</p>}
                  </div>
                  {user?.phoneNumber && (
                    <div className="info-footer">
                      <div className="phone-info"><FiPhone /><span>{user.phoneNumber}</span></div>
                      <div className="copy-icon-circle" onClick={() => handleCopy(user.phoneNumber, 'userPhone')}>
                        {renderCopyIcon('userPhone')}
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6} className="mb-4">
              <Card className="info-card">
                <Card.Body>
                  <div className="info-header"><span className="info-title">Order Details</span></div>
                  <div className="order-details-content">
                    {order.orderId && (
                      <div className="order-detail-row">
                        <p className="info-label">Order ID</p>
                        <div className="order-detail-value-with-copy">
                          <p className="info-value">{order.orderId}</p>
                          <div className="copy-icon-circle" onClick={() => handleCopy(order.orderId, 'orderId')}>
                            {renderCopyIcon('orderId')}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="order-detail-row"><p className="info-label">Status</p><p className="info-value">{getStatusText(order)}</p></div>
                    {chef?.instructions && (
                      <div className="order-detail-row">
                        <p className="info-label">Pick-up Instructions</p>
                        <p className="info-value">{chef.instructions}</p>
                      </div>
                    )}
                    {order.driverInstructions && (
                      <div className="order-detail-row">
                        <p className="info-label">Delivery Instructions</p>
                        <p className="info-value">{order.driverInstructions}</p>
                      </div>
                    )}
                    {order.currentDeliveryStatus != null && (
                      <div className="order-detail-row">
                        <p className="info-label">Delivery ID</p>
                        {order.delivery_id && !isCancelledOrUnassigned ? (
                          <div className="order-detail-value-with-copy">
                            <p className="info-value">{order.delivery_id}</p>
                            <div className="copy-icon-circle" onClick={() => handleCopy(order.delivery_id, 'deliveryId')}>
                              {renderCopyIcon('deliveryId')}
                            </div>
                          </div>
                        ) : (
                          <p className="info-value">N/A</p>
                        )}
                      </div>
                    )}
                    {(order.currentDeliveryStatus?.events?.[0]?.type === "DELIVERED" || order.currentDeliveryStatus?.events?.[0]?.type === "PROOF_OF_DELIVERY") &&
                      order.currentDeliveryStatus?.events?.[0]?.dropoff_image_details?.photo_url && (
                        <div className="order-detail-row">
                          <a
                            href={order.currentDeliveryStatus.events[0].dropoff_image_details.photo_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer', fontSize: '14px' }}
                          >
                            Delivery Photo URL
                          </a>
                        </div>
                      )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default DishDetails;